import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { useAlert, useApi } from '@app/hooks';
import { TableBase } from '@app/components/common/table/table-base';
import Dialog from '@app/components/common/modals/dialog';
import { SwitchField, TextField } from '@app/components/common';
import { UnitSku } from '@app/api/models/sku';

import { useColumns } from './columns';

type UnitDraft = Partial<UnitSku>;

export const SkuUnitsTable: FC = () => {
  const api = useApi();
  const { error } = useAlert();

  const [units, setUnits] = useState<UnitSku[]>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<UnitDraft>();
  const [currentDialog, setCurrentDialog] = useState<'edit' | 'create'>();

  const fetchList = useCallback(() => {
    setLoading(true);
    api.sku.getUnits().then(({ data }) => {
      setLoading(false);
      setUnits(data || []);
    });
  }, [api]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = (unit: UnitSku) => {
    setCurrent(unit);
    setCurrentDialog('edit');
  };

  const columns = useColumns({ onEdit });

  const onClose = () => {
    setCurrent(undefined);
    setCurrentDialog(undefined);
  };

  const onSubmit = (unit?: UnitDraft) => async () => {
    if (!unit?.name) {
      error('Введите название');
      return;
    }
    setLoading(true);
    if (unit.id) {
      await api.sku.updateUnit({
        id: unit.id,
        name: unit.name,
        is_active: !!unit.is_active,
      });
    } else {
      await api.sku.createUnit({
        name: unit.name,
        is_active: unit.is_active ?? true,
      });
    }
    fetchList();
    onClose();
  };

  return (
    <>
      <TableBase<UnitSku>
        title="Единицы измерения"
        data={units}
        columns={columns}
        loading={loading}
        onCreateClick={() => {
          setCurrent({ is_active: true });
          setCurrentDialog('create');
        }}
      />
      <Dialog
        open={!!currentDialog}
        onClose={onClose}
        title={currentDialog === 'edit' ? 'Редактирование единицы измерения' : 'Создание единицы измерения'}
      >
        <Flex gap={2} flexDirection="column">
          <TextField
            id="name"
            label="Название"
            required
            value={current?.name || ''}
            onChange={(value) =>
              setCurrent({
                ...current,
                name: value,
              })
            }
          />
          <SwitchField
            label="Активна"
            value={current?.is_active}
            onChange={(value) =>
              setCurrent({
                ...current,
                is_active: value,
              })
            }
            id="is_active"
          />
          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="outline" colorScheme="gray" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="solid" colorScheme="brand" onClick={onSubmit(current)}>
              Сохранить
            </Button>
          </Flex>
        </Flex>
      </Dialog>
    </>
  );
};

export default SkuUnitsTable;
